import React, {
  FC,
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {Path, Transforms} from 'slate';
import {
  ReactEditor,
  useReadOnly,
  useSelected,
  useSlateStatic,
} from 'slate-react';

import {captureError} from '../../../../integrations';
import {useWhole} from '../../../../state/views/hooks';
import * as SectionTypes from '../../../../state/views/section/types';
import {ErrorBoundary} from '../../../ErrorBoundary';
import {WBSlateReduxBridgeContext} from '../../WBSlateReduxBridge';
import * as S from './PanelGridElement.styles';
import {PanelGridError} from './PanelGridError';
import {PanelGrid as PanelGridType} from './types';

export const PanelGrid: FC<{
  element: PanelGridType;
  partRef: SectionTypes.Ref;
  updateDenormalizedOnNormalizedUpdate: MutableRefObject<boolean>;
  renormalizeOnDenormalizedUpdate: MutableRefObject<boolean>;
}> = ({
  element,
  partRef,
  updateDenormalizedOnNormalizedUpdate,
  renormalizeOnDenormalizedUpdate,
}) => {
  const {projectName, entityName, viewRef, panelSettingsRef} = useContext(
    WBSlateReduxBridgeContext
  );
  if (viewRef == null) {
    throw new Error(`panel grid rendered with null view ref`);
  }
  if (panelSettingsRef == null) {
    throw new Error(`panel grid rendered with null panel settings ref`);
  }
  const selected = useSelected();
  const whole = useWhole(partRef);
  const readOnly = useReadOnly();
  const editor = useSlateStatic();

  const pathRef = useRef<Path>(ReactEditor.findPath(editor, element));
  pathRef.current = ReactEditor.findPath(editor, element);

  /** On a normal change, quietly make a saved change. */
  useEffect(() => {
    if (updateDenormalizedOnNormalizedUpdate.current) {
      renormalizeOnDenormalizedUpdate.current = false;
      Transforms.setNodes(
        editor,
        {metadata: whole},
        {
          at: pathRef.current,
        }
      );
    }
    updateDenormalizedOnNormalizedUpdate.current = true;
  }, [
    editor,
    pathRef,
    renormalizeOnDenormalizedUpdate,
    updateDenormalizedOnNormalizedUpdate,
    whole,
  ]);

  // logics for ErrorBoundary wrapping panel grids
  const errorParams = useMemo(
    () => ({entityName, projectName}),
    [entityName, projectName]
  );

  const logError = useCallback(
    (error: any) => {
      const key = readOnly ? 'PanelGridView' : 'PanelGridEdit';
      window.analytics?.track(`${key} error`, {
        errorParams,
        errorMessage: error.message,
        errorName: error.name,
        errorStack: error.stack,
      });
      captureError(error, `${key} ErrorBoundary error`);
    },
    [errorParams, readOnly]
  );

  const renderPanelGridError = useCallback(() => <PanelGridError />, []);

  return (
    <ErrorBoundary renderError={renderPanelGridError} onError={logError}>
      <S.ReportPanelGridSection
        $selected={selected}
        entityName={entityName}
        projectName={projectName}
        panelSettingsRef={panelSettingsRef}
        sectionRef={partRef}
        viewRef={viewRef}
        readOnly={readOnly}
      />
    </ErrorBoundary>
  );
};
